import React, { useState, useEffect } from "react";
import {
  Paper,
  Text,
  TextInput,
  Group,
  ActionIcon,
  Button,
  Stack,
  Badge,
  Alert,
  Loader,
} from "@mantine/core";
import {
  IconPencil,
  IconCheck,
  IconX,
  IconAlertCircle,
} from "@tabler/icons-react";
import { BusinessInfo } from "../../../types/context";
import { useTemplate } from "../../../context/TemplateContext";

interface WebsiteURLBuiltInDomainProps {
  onDomainTypeChange?: (domainType: "custom") => void;
  businessInfo?: BusinessInfo | null;
}

const WebsiteURLBuiltInDomain: React.FC<WebsiteURLBuiltInDomainProps> = ({
  onDomainTypeChange,
  businessInfo,
}) => {
  const salonId = businessInfo?.id || "";
  const businessNameWithoutSpaces =
    businessInfo?.salon_name.replace(/[^a-zA-Z0-9]/g, "") || "";

  const {
    checkSubdomainAvailability,
    claimUpdateSubdomain,
    getCurrentSubdomain,
    refreshDraftSubdomain,
  } = useTemplate();

  // State
  const [subdomain, setSubdomain] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [currentSubdomain, setCurrentSubdomain] = useState<string | null>(null);
  const [fullDomain, setFullDomain] = useState<string | null>(null);
  // Start loading as true if we have a salonId (will fetch on mount)
  const [isLoading, setIsLoading] = useState(!!salonId);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState<{
    available: boolean | null;
    reason?: string;
    message?: string;
  }>({ available: null });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch current subdomain on mount
  useEffect(() => {
    if (salonId) {
      fetchCurrentSubdomain();
    }
  }, [salonId]);

  const fetchCurrentSubdomain = async () => {
    if (!salonId) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await getCurrentSubdomain(salonId, "live");
      setCurrentSubdomain(response.subdomain);
      setFullDomain(response.full_domain);
      setSubdomain(response.subdomain || "");
    } catch (err: any) {
      // 404 is expected if no subdomain is set yet
      if (err?.status !== 404 && err?.response?.status !== 404) {
        console.error("Error fetching current subdomain:", err);
        setError("Failed to load current subdomain");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSubdomain(
      currentSubdomain || businessNameWithoutSpaces?.toLowerCase() || "",
    );
    setAvailabilityStatus({ available: null });
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSubdomain(currentSubdomain || "");
    setAvailabilityStatus({ available: null });
    setError(null);
    setSuccess(null);
  };

  const handleCheckAvailability = async () => {
    if (!subdomain || !subdomain.trim()) {
      setError("Please enter a subdomain name");
      return;
    }

    // Validate format: lowercase alphanumeric and hyphens only
    const subdomainRegex = /^[a-z0-9-]+$/;
    if (!subdomainRegex.test(subdomain.toLowerCase())) {
      setError(
        "Subdomain can only contain lowercase letters, numbers, and hyphens",
      );
      return;
    }

    try {
      setIsCheckingAvailability(true);
      setError(null);
      setSuccess(null);

      const response = await checkSubdomainAvailability({
        name: subdomain.toLowerCase(),
        environment: "live",
      });

      if (response.available) {
        setAvailabilityStatus({
          available: true,
          message: "This subdomain is available!",
        });
        setSuccess("Subdomain is available");
      } else {
        setAvailabilityStatus({
          available: false,
          reason: response.reason,
          message:
            response.reason === "taken"
              ? "This subdomain is already taken"
              : response.reason === "reserved"
                ? "This subdomain is reserved"
                : "Invalid subdomain format",
        });
        setError(
          response.reason === "taken"
            ? "This subdomain is already taken. Please choose another."
            : response.reason === "reserved"
              ? "This subdomain is reserved. Please choose another."
              : "Invalid subdomain format",
        );
      }
    } catch (err) {
      console.error("Error checking subdomain availability:", err);
      setError("Failed to check availability. Please try again.");
      setAvailabilityStatus({ available: null });
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const handleSave = async () => {
    if (!subdomain || !subdomain.trim()) {
      setError("Please enter a subdomain name");
      return;
    }

    if (availabilityStatus.available === null) {
      setError("Please check availability first");
      return;
    }

    if (!availabilityStatus.available) {
      setError("Subdomain is not available. Please choose another.");
      return;
    }

    if (!salonId) {
      setError("Salon ID is missing");
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const response = await claimUpdateSubdomain(salonId, {
        subdomain: subdomain.toLowerCase(),
        environment: "live",
      });

      // Use the subdomain from response, or fallback to the input value
      const savedSubdomain = response.subdomain || subdomain.toLowerCase();
      // Construct full domain if not returned by API
      const savedFullDomain =
        response.full_domain || `${savedSubdomain}.getpagoda.site`;

      setCurrentSubdomain(savedSubdomain);
      setFullDomain(savedFullDomain);
      setIsEditing(false);
      setSuccess(
        `Subdomain updated! Your website will be available at ${savedFullDomain}`,
      );
      setAvailabilityStatus({ available: null });

      // Refresh draft subdomain in TemplateContext so preview button works
      if (refreshDraftSubdomain) {
        await refreshDraftSubdomain();
      }
    } catch (err: any) {
      console.error("Error saving subdomain:", err);
      const errorMessage =
        err?.message || "Failed to save subdomain. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Paper p="md">
      <Text size="xl" fw={700} mb="md">
        Your URL
      </Text>

      <Text c="dimmed" mb="lg">
        Set and manage the web address where people can find your site.
      </Text>

      <Group gap="md" mb="lg">
        <Button variant="filled" color="blue" size="md">
          Built-In Domain
        </Button>
        <Button
          variant="outline"
          color="gray"
          size="md"
          onClick={() => onDomainTypeChange?.("custom")}
        >
          Use a Custom Domain
        </Button>
      </Group>

      {isLoading ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
          <Text c="dimmed">Loading subdomain...</Text>
        </Group>
      ) : (
        <Stack gap="md">
          {/* Current Subdomain Display */}
          {!isEditing && currentSubdomain && (
            <Group>
              <TextInput
                value={currentSubdomain}
                readOnly
                style={{ flex: 1 }}
                rightSection={
                  <ActionIcon
                    variant="light"
                    color="gray"
                    onClick={handleEdit}
                    title="Edit subdomain"
                  >
                    <IconPencil size={16} />
                  </ActionIcon>
                }
              />
              {fullDomain && (
                <Badge color="blue" variant="light" size="lg">
                  {fullDomain}
                </Badge>
              )}
            </Group>
          )}

          {/* Edit Mode */}
          {isEditing && (
            <Stack gap="sm">
              <Group>
                <TextInput
                  placeholder={businessNameWithoutSpaces.toLowerCase()}
                  value={subdomain}
                  onChange={(e) => {
                    setSubdomain(e.target.value.toLowerCase());
                    setAvailabilityStatus({ available: null });
                    setError(null);
                    setSuccess(null);
                  }}
                  style={{ flex: 1 }}
                  rightSection={
                    <Group gap="xs" style={{ paddingRight: "8px" }}>
                      <Text c="dimmed" size="sm">
                        .getpagoda.site
                      </Text>
                    </Group>
                  }
                  rightSectionWidth={140}
                />
              </Group>

              <Group>
                <Button
                  variant="outline"
                  onClick={handleCheckAvailability}
                  loading={isCheckingAvailability}
                  disabled={!subdomain || !subdomain.trim()}
                >
                  Check Availability
                </Button>
                <Button variant="subtle" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  loading={isSaving}
                  disabled={
                    !subdomain ||
                    !subdomain.trim() ||
                    availabilityStatus.available !== true
                  }
                >
                  Save
                </Button>
              </Group>

              {/* Availability Status */}
              {availabilityStatus.available !== null && (
                <Group gap="xs">
                  {availabilityStatus.available ? (
                    <>
                      <IconCheck size={16} color="green" />
                      <Text c="green" size="sm">
                        {availabilityStatus.message}
                      </Text>
                    </>
                  ) : (
                    <>
                      <IconX size={16} color="red" />
                      <Text c="red" size="sm">
                        {availabilityStatus.message}
                      </Text>
                    </>
                  )}
                </Group>
              )}
            </Stack>
          )}

          {/* No Subdomain Set - Show Input */}
          {!isEditing && !currentSubdomain && (
            <Group>
              <TextInput
                placeholder={businessNameWithoutSpaces.toLowerCase()}
                value={subdomain}
                onChange={(e) => {
                  setSubdomain(e.target.value.toLowerCase());
                  setAvailabilityStatus({ available: null });
                  setError(null);
                }}
                style={{ flex: 1 }}
                rightSection={
                  <Group gap="xs" style={{ paddingRight: "8px" }}>
                    <Text c="dimmed" size="sm">
                      .pagoda.site
                    </Text>
                  </Group>
                }
                rightSectionWidth={120}
              />
              <Button
                variant="outline"
                onClick={handleCheckAvailability}
                loading={isCheckingAvailability}
                disabled={!subdomain || !subdomain.trim()}
              >
                Check Availability
              </Button>
              <Button
                onClick={handleSave}
                loading={isSaving}
                disabled={
                  !subdomain ||
                  !subdomain.trim() ||
                  availabilityStatus.available !== true
                }
              >
                Save
              </Button>
            </Group>
          )}

          {/* Availability Status for No Subdomain State */}
          {!isEditing &&
            !currentSubdomain &&
            availabilityStatus.available !== null && (
              <Group gap="xs">
                {availabilityStatus.available ? (
                  <>
                    <IconCheck size={16} color="green" />
                    <Text c="green" size="sm">
                      {availabilityStatus.message}
                    </Text>
                  </>
                ) : (
                  <>
                    <IconX size={16} color="red" />
                    <Text c="red" size="sm">
                      {availabilityStatus.message}
                    </Text>
                  </>
                )}
              </Group>
            )}

          {/* Error Message */}
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {error}
            </Alert>
          )}

          {/* Success Message */}
          {success && (
            <Alert icon={<IconCheck size={16} />} color="green">
              {success}
            </Alert>
          )}

          {/* Info about subdomain */}
          {currentSubdomain && fullDomain && !isEditing && (
            <Text size="sm" c="dimmed">
              Your website will be available at:{" "}
              <Text component="span" fw={500} c="blue">
                {fullDomain}
              </Text>
            </Text>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default WebsiteURLBuiltInDomain;
